/**
 * 公開・権限の管理(docs/20 §2.5・§3)。
 *
 * 読み取りは Firestore(role クレームがあれば読める): `contentReleases` / `contentMeta/current` /
 * `adminAudit`。**書込みはすべて管理用 callable**(管理画面からも Firestore へ直接書かない。docs/20 §1)。
 *
 *   adminWhoAmI()                               → { uid, email, role }
 *   adminSetRole({ email, role|null, operationId })(owner)
 *   cmsCreatePreview({ note, operationId })      → { releaseId, warnings, counts } / 検証エラー
 *   cmsPublish({ releaseId, operationId })
 *   cmsRollback({ releaseId, operationId })
 *
 * `operationId` は1回の操作(ダイアログ1回)につき1つ。通信が切れて押し直しても同じ ID を送り、
 * サーバー側で二重に実行されないようにする(冪等)。
 */
import type { ContentReleaseDoc, ContentReleaseStatus } from '@paltyping/content-schema';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  type Firestore,
} from 'firebase/firestore';
import { httpsCallable, type Functions } from 'firebase/functions';
import type { AdminRole } from './adminAuth';

export const CONTENT_RELEASES_COLLECTION = 'contentReleases';
export const CONTENT_META_DOC = ['contentMeta', 'current'] as const;
export const ADMIN_AUDIT_COLLECTION = 'adminAudit';
export const RELEASE_LIST_LIMIT = 50;
export const AUDIT_LIST_LIMIT = 100;

export const RELEASE_STATUS_LABELS: Readonly<Record<ContentReleaseStatus, string>> = {
  preview: 'プレビュー',
  published: '公開済み',
  archived: '過去版',
};

/** 画面で扱う公開版(時刻は表示用の文字列に揃える)。 */
export interface ReleaseRecord extends Omit<ContentReleaseDoc, 'createdAt'> {
  readonly createdAt: string;
  readonly publishedAt: string | null;
}

export interface ContentMeta {
  readonly releaseId: string;
  readonly publishedAt: string | null;
  readonly previous: { readonly releaseId: string; readonly until: string | null } | null;
}

export interface AuditEntry {
  readonly id: string;
  readonly at: string;
  readonly uid: string;
  readonly email: string | null;
  readonly action: string;
  readonly target: string;
  readonly before: unknown;
  readonly after: unknown;
  readonly operationId: string;
}

export interface CreatePreviewResult {
  readonly releaseId: string;
  readonly warnings: readonly string[];
  readonly counts: Readonly<Record<string, number>>;
}

export interface WhoAmI {
  readonly uid: string;
  readonly email: string | null;
  readonly role: AdminRole | null;
}

// ── 値の正規化 ─────────────────────────────────────────────

/** Firestore の Timestamp / ISO 文字列 / epoch ms を ISO 文字列へ。読めなければ null。 */
export function toIsoString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return new Date(value).toISOString();
  if (typeof value === 'object' && 'toDate' in value) {
    const toDate = (value as { toDate: unknown }).toDate;
    if (typeof toDate === 'function') {
      const date = (toDate as () => Date).call(value);
      return date instanceof Date && !Number.isNaN(date.getTime()) ? date.toISOString() : null;
    }
  }
  return null;
}

/** 表示用の日時(ローカル時刻 `YYYY-MM-DD HH:MM`)。 */
export function formatDateTime(iso: string | null): string {
  if (iso === null) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

function asStringRecord(value: unknown): Record<string, string> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return {};
  const out: Record<string, string> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry === 'string') out[key] = entry;
  }
  return out;
}

function asNumberRecord(value: unknown): Record<string, number> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return {};
  const out: Record<string, number> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry === 'number' && Number.isFinite(entry)) out[key] = entry;
  }
  return out;
}

function asStatus(value: unknown): ContentReleaseStatus {
  return value === 'published' || value === 'archived' ? value : 'preview';
}

export function toReleaseRecord(id: string, data: Record<string, unknown>): ReleaseRecord {
  return {
    releaseId: typeof data.releaseId === 'string' ? data.releaseId : id,
    createdAt: toIsoString(data.createdAt) ?? '',
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
    note: typeof data.note === 'string' ? data.note : '',
    status: asStatus(data.status),
    basedOn: typeof data.basedOn === 'string' ? data.basedOn : null,
    files: asStringRecord(data.files),
    counts: asNumberRecord(data.counts),
    publishedAt: toIsoString(data.publishedAt),
  };
}

export function toContentMeta(data: Record<string, unknown> | undefined): ContentMeta | null {
  if (!data || typeof data.releaseId !== 'string') return null;
  const previous = data.previous as { releaseId?: unknown; until?: unknown } | null | undefined;
  return {
    releaseId: data.releaseId,
    publishedAt: toIsoString(data.publishedAt),
    previous:
      previous && typeof previous.releaseId === 'string'
        ? { releaseId: previous.releaseId, until: toIsoString(previous.until) }
        : null,
  };
}

export function toAuditEntry(id: string, data: Record<string, unknown>): AuditEntry {
  return {
    id,
    at: toIsoString(data.at) ?? '',
    uid: typeof data.uid === 'string' ? data.uid : '',
    email: typeof data.email === 'string' ? data.email : null,
    action: typeof data.action === 'string' ? data.action : '',
    target: typeof data.target === 'string' ? data.target : JSON.stringify(data.target ?? ''),
    before: data.before ?? null,
    after: data.after ?? null,
    operationId: typeof data.operationId === 'string' ? data.operationId : '',
  };
}

// ── 版の比較 ───────────────────────────────────────────────

export interface ReleaseDiff {
  readonly added: readonly string[];
  readonly removed: readonly string[];
  readonly changed: readonly string[];
  readonly unchanged: number;
}

/** 2つの版の files(パス → sha256)を比べて、変わったファイルを並べる。 */
export function diffReleaseFiles(
  from: Readonly<Record<string, string>>,
  to: Readonly<Record<string, string>>,
): ReleaseDiff {
  const added: string[] = [];
  const removed: string[] = [];
  const changed: string[] = [];
  let unchanged = 0;
  for (const path of Object.keys(to).sort()) {
    if (!(path in from)) added.push(path);
    else if (from[path] !== to[path]) changed.push(path);
    else unchanged += 1;
  }
  for (const path of Object.keys(from).sort()) {
    if (!(path in to)) removed.push(path);
  }
  return { added, removed, changed, unchanged };
}

/** プレビュー版をゲームで試すURL(docs/20 §2.4)。 */
export function previewGameUrl(webAppUrl: string, releaseId: string): string {
  return `${webAppUrl}/?contentRelease=${encodeURIComponent(releaseId)}`;
}

/** 1回の操作の ID(冪等キー)。 */
export function newOperationId(): string {
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi?.randomUUID === 'function') return cryptoApi.randomUUID();
  return `op_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

// ── エラー ─────────────────────────────────────────────────

export type CmsErrorCode =
  'revision-conflict' | 'permission-denied' | 'validation' | 'network' | 'unknown';

const CMS_ERROR_MESSAGES: Readonly<Record<CmsErrorCode, string>> = {
  'revision-conflict': 'ほかの人が更新しました。読み直してください。',
  'permission-denied': 'この操作をする権限がありません。',
  validation: '検証エラーがあるため実行できませんでした。',
  network:
    'サーバーに接続できませんでした。時間をおいてやり直してください(同じ操作として再送されます)。',
  unknown: '操作に失敗しました。',
};

export class CmsError extends Error {
  constructor(
    readonly code: CmsErrorCode,
    message: string,
    readonly errors: readonly string[] = [],
    readonly warnings: readonly string[] = [],
    override readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'CmsError';
  }
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : [];
}

/** callable の例外を画面用に分類する(SDK の型に依存せず形だけを見る)。 */
export function toCmsError(error: unknown): CmsError {
  if (error instanceof CmsError) return error;
  const raw = (typeof error === 'object' && error !== null ? error : {}) as {
    code?: unknown;
    message?: unknown;
    details?: unknown;
  };
  const code = typeof raw.code === 'string' ? raw.code.replace(/^functions\//, '') : '';
  const details = (typeof raw.details === 'object' && raw.details !== null ? raw.details : {}) as {
    reason?: unknown;
    code?: unknown;
    errors?: unknown;
    warnings?: unknown;
  };
  const reason =
    typeof details.reason === 'string'
      ? details.reason
      : typeof details.code === 'string'
        ? details.code
        : '';
  const errors = stringList(details.errors);
  const warnings = stringList(details.warnings);
  const serverMessage = typeof raw.message === 'string' ? raw.message : '';
  if (code === 'revision-conflict' || reason === 'revision-conflict') {
    return new CmsError(
      'revision-conflict',
      CMS_ERROR_MESSAGES['revision-conflict'],
      [],
      [],
      error,
    );
  }
  if (
    code === 'permission-denied' ||
    reason === 'permission-denied' ||
    code === 'unauthenticated'
  ) {
    return new CmsError(
      'permission-denied',
      CMS_ERROR_MESSAGES['permission-denied'],
      [],
      [],
      error,
    );
  }
  if (errors.length > 0) {
    return new CmsError('validation', CMS_ERROR_MESSAGES.validation, errors, warnings, error);
  }
  if (code === 'unavailable' || code === 'deadline-exceeded' || code === 'internal') {
    return new CmsError('network', CMS_ERROR_MESSAGES.network, [], [], error);
  }
  return new CmsError(
    'unknown',
    serverMessage === ''
      ? CMS_ERROR_MESSAGES.unknown
      : `${CMS_ERROR_MESSAGES.unknown}(${serverMessage})`,
    [],
    warnings,
    error,
  );
}

// ── 呼び出し口 ─────────────────────────────────────────────

/** 管理用 callable(テストでは差し替える)。 */
export interface CmsCallables {
  whoAmI(): Promise<WhoAmI>;
  createPreview(note: string, operationId: string): Promise<CreatePreviewResult>;
  publish(releaseId: string, operationId: string): Promise<void>;
  rollback(releaseId: string, operationId: string): Promise<void>;
  setRole(email: string, role: AdminRole | null, operationId: string): Promise<void>;
}

/** Firestore からの読み取り(テストでは差し替える)。 */
export interface CmsReader {
  listReleases(): Promise<readonly ReleaseRecord[]>;
  currentMeta(): Promise<ContentMeta | null>;
  listAudit(): Promise<readonly AuditEntry[]>;
}

type Invoke = (name: string, data: unknown) => Promise<unknown>;

/** 例外を `CmsError` に揃えて呼ぶ。 */
export function createCmsCallablesWith(invoke: Invoke): CmsCallables {
  const call = async (name: string, data: unknown): Promise<unknown> => {
    try {
      return await invoke(name, data);
    } catch (error) {
      throw toCmsError(error);
    }
  };
  return {
    whoAmI: async () => (await call('adminWhoAmI', {})) as WhoAmI,
    createPreview: async (note, operationId) => {
      const result = (await call('cmsCreatePreview', {
        note,
        operationId,
      })) as Partial<CreatePreviewResult>;
      return {
        releaseId: String(result.releaseId ?? ''),
        warnings: stringList(result.warnings),
        counts: asNumberRecord(result.counts),
      };
    },
    publish: async (releaseId, operationId) => {
      await call('cmsPublish', { releaseId, operationId });
    },
    rollback: async (releaseId, operationId) => {
      await call('cmsRollback', { releaseId, operationId });
    },
    setRole: async (email, role, operationId) => {
      await call('adminSetRole', { email, role, operationId });
    },
  };
}

export function createCmsCallables(functions: Functions): CmsCallables {
  return createCmsCallablesWith(
    async (name, data) => (await httpsCallable(functions, name)(data)).data,
  );
}

export function createCmsReader(db: Firestore): CmsReader {
  return {
    listReleases: async () => {
      const snapshot = await getDocs(
        query(
          collection(db, CONTENT_RELEASES_COLLECTION),
          orderBy('createdAt', 'desc'),
          limit(RELEASE_LIST_LIMIT),
        ),
      );
      return snapshot.docs.map((entry) => toReleaseRecord(entry.id, entry.data()));
    },
    currentMeta: async () => {
      const snapshot = await getDoc(doc(db, ...CONTENT_META_DOC));
      return snapshot.exists() ? toContentMeta(snapshot.data()) : null;
    },
    listAudit: async () => {
      const snapshot = await getDocs(
        query(
          collection(db, ADMIN_AUDIT_COLLECTION),
          orderBy('at', 'desc'),
          limit(AUDIT_LIST_LIMIT),
        ),
      );
      return snapshot.docs.map((entry) => toAuditEntry(entry.id, entry.data()));
    },
  };
}
